import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';

import {
  BLOB_RESPONSE_TYPE,
  CommonTransformationService,
  DateService,
  FinancialReportType,
  Format, OverTimeConfigParams,
  TimesheetAction,
  TimesheetLaborDistributionType, TimesheetOvertimeConfig, UserSearch
} from '@app/shared';
import {Timesheet} from "@app/finance/time/timesheet";
import {TimesheetAdminSearchOption, TimesheetNotificationType} from "@app/finance/time/timesheet-admin/timesheet-admin";


@Injectable()
export class TimesheetAdminDataService {

  constructor(private http: HttpClient, private transform: CommonTransformationService, private dateService: DateService) {
  }

  generateSearchOptionURL(searchOption: TimesheetAdminSearchOption, url: string){
    if (searchOption) {
      if (searchOption.year) {
        url += `&year=${searchOption.year}`;
      }
      if (searchOption.period && searchOption.period.id) {
        url += `&periodId=${searchOption.period.id}`;
      }
      if (searchOption.userType) {
        url += `&userType=${searchOption.userType}`;
      }
      if (searchOption.status) {
        url += `&status=${searchOption.status}`;
      }
      if (searchOption.showTerminatedUsers) {
        url += `&showTerminatedUsers=${searchOption.showTerminatedUsers}`;
      }
      if (!searchOption.showTerminatedUsers) {
        url += `&showTerminatedUsers=${searchOption.showTerminatedUsers}`;
      }
      if (searchOption.showTerminatedUsers && searchOption.terminationDate) {
        url += `&terminationDate=${searchOption.terminationDate}`;
      }
    }
    return url;
  }

  getOverTimeConfig(userId?: number, year?: string): Observable<TimesheetOvertimeConfig[]>{
    let url = `/finance/timesheets/overtime-config?`;
    if(userId) {
      url += `&userId=${userId}`;
    }
    if(year) {
      url += `&year=${year}`;
    }
    return this.http.get<TimesheetOvertimeConfig[]>(url);
  }

  generateOverTimeConfig(configParams: OverTimeConfigParams): Observable<TimesheetOvertimeConfig[]>{
    let url = `/finance/timesheets/overtime-config`;
    return this.http.post<TimesheetOvertimeConfig[]>(url, configParams);
  }

  getUsersOnAlternateSchedule(): Observable<UserSearch[]> {
    let url = `/finance/timesheets/users-alternate-workweek`;
    return this.http.get<UserSearch[]>(url);
  }

  getTimesheetAdminSummary(searchOption: TimesheetAdminSearchOption): Observable<any> {
    let url = `/finance/timesheets/admin-summary?`;
    url = this.generateSearchOptionURL(searchOption, url);
    return this.http.get<any>(url);
  }

  getTimesheetAdminSummaryByPeriod(periodId: number): Observable<any> {
    let url = `/finance/timesheets/admin-summary?periodId=${periodId}`;
    return this.http.get<any>(url);
  }

  initializeAnnualLeaveBalances(year: string): Observable<any> {
    let url = `/finance/timesheets/annual-leave?year=${year}`;

    return this.http.post(url, '');
  }

  sendReminderNotification(userType: TimesheetNotificationType): Observable<any> {
    let url;
    if (userType == TimesheetNotificationType.User) {
      url = `/finance/timesheets/notifications/user`;
    } else {
      url = `/finance/timesheets/notifications/manager-indv-mail`;
    }
    return this.http.post(url, '');
  }

  closeTimesheetsByPeriod(periodId: number): Observable<any> {
    let url = `/finance/timesheets/close-period`;
    return this.http.post(url, '');
  }

  closeTimesheets(processingDate: Date): Observable<any> {
    let processingDateObject;
    if (!processingDate) {
      processingDateObject = this.dateService.convertDateToStringDate(new Date());
    } else {
      processingDateObject = this.dateService.convertDateToStringDate(processingDate);
    }
    let url = `/finance/timesheets/close-period?localDate=${processingDateObject}`;
    return this.http.post(url, '');
  }

  processTimesheetsByPeriod(periodId: number): Observable<any> {
    // ToDo jC: need to specify correct URL when it becomes available
    let url = `/finance/timesheets/process-closed`;

    return this.http.post(url, '');
  }

  processTimesheets(processingDate: Date): Observable<any> {
    let processingDateObject;
    if (!processingDate) {
      processingDateObject = this.dateService.convertDateToStringDate(new Date());
    } else {
      processingDateObject = this.dateService.convertDateToStringDate(processingDate);
    }
    let url = `/finance/timesheets/process-closed?localDate=${processingDateObject}`;
    return this.http.post(url, '');
  }

  getLaborDistributionReport(timesheetLaborDistributionType: TimesheetLaborDistributionType, periodId: number): Observable<any> {
    let url = `/pdf/labor-distribution-pdf?timesheetLaborDistributionType=${timesheetLaborDistributionType}&periodId=${periodId}`;
    let headers = {observe: 'response', responseType: BLOB_RESPONSE_TYPE} as any;
    return this.http.get<any>(url, headers);
  }

  getLaborDistributionByAccountReport( periodId: number): Observable<any> {
    let url = `/accounting/labor-distribution-pdf?timesheetPeriodId=${periodId}&financialReportType=${FinancialReportType.LaborDistributionByAccount}`;
    let headers = { observe: 'response', responseType: BLOB_RESPONSE_TYPE } as any;
    return this.http.get<any>(url, headers);
  }

  rejectTimesheet(timesheetAction: TimesheetAction): Observable<Timesheet> {
    const url = `/finance/timesheets/reject`;
    return this.http.post<any>(url, timesheetAction);
  }

  validateTimesheet(timesheetId: number): Observable<any> {
    let today = this.dateService.convertDateToStringDate(new Date());
    const url = `/finance/timesheets/validate/${timesheetId}?localDate=${today}`;
    return this.http.post(url, null);
  }

  validateTimesheets(timesheetIds: number[], searchOption: TimesheetAdminSearchOption): Observable<any> {
    let today = this.dateService.convertDateToStringDate(new Date());
    let url = `/finance/timesheets/validate-all?timesheetIds=${timesheetIds}&localDate=${today}`;
    url = this.generateSearchOptionURL(searchOption, url);
    return this.http.post(url, null);
  }
}
